import { FormulaOperator } from '@doss/prisma/types'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import utc from 'dayjs/plugin/utc.js'
import _ from 'lodash'
import {
  FormulaConditionInput,
  FormulaInput,
  FormulaValueInput,
  LogicExpressionInput,
} from '../antlr/utils/formula.js'
import {
  isColumnFormulaOperator,
  isRoundingFormulaOperator,
  isRowFormulaOperator,
} from '../utils/formula.js'
import { DATE_FILTER_STRING_FORMAT, dayJsToMidnightUTCDateTimeString } from './dates.js'
import {
  DATE_UNITS,
  FormulaValueType,
  inferFormulaOutputType,
  isValidDateUnit,
  resolveBinaryArithmetic,
} from './formula-operator-specs.js'
import { resolveLogicExpressionFromParsed } from './logic-expression-resolver.js'
import { evaluateLogicExpression, getHomogeneousValues } from './logic-expression.js'
import { safeDivide, safeMultiply, safeRound, safeSubtract, safeSum } from './numbers.js'
import { ActionData, ActionDataArray } from './types/action-data.js'
import { UserEnteredLogicInputs } from './types/logic.js'
import { DossDataFormat, getTableCellPrimitiveValue } from './types/table-cell.js'

dayjs.extend(utc)
dayjs.extend(customParseFormat)

type FormulaResultValue = number | string

/**
 * Coerces a resolved formula value to a number, parsing numeric strings.
 * @throws Error if the value is a non-numeric string
 */
function toFormulaNumber(value: FormulaResultValue): number {
  if (typeof value === 'number') {
    return value
  }
  const parsed = parseFloat(value)
  if (isNaN(parsed)) {
    throw new Error(`Cannot convert form input "${value}" to number`)
  }
  return parsed
}

/**
 * Coerces a resolved formula value to a string, for text operators like CONCAT.
 *
 * CONCAT's operands are type-checked to be TEXT (string-valued form inputs), so the only way this
 * sees a number is `resolveFormInputValue`'s "no value" fallback of `0` for an unset form input —
 * treat that the same as an entered empty string.
 *
 * @throws Error if the value is a non-zero number (an actual type mismatch, not a missing input)
 */
function toFormulaString(value: FormulaResultValue): string {
  if (typeof value === 'string') {
    return value
  }
  if (value === 0) {
    return ''
  }
  throw new Error(`Expected a string formula value, got number "${value}"`)
}

/**
 * Evaluates a parsed formula and returns the numeric result.
 *
 * Supports two types of formulas:
 * 1. Row formulas (ADDITION, SUBTRACTION, MULTIPLICATION, DIVISION, ROUND, CEIL, FLOOR)
 *    - Operate on individual numeric values
 *    - Example: {inputA} + {inputB}
 *
 * 2. Column formulas (SUM, COUNT, MIN, MAX, SUMIF, COUNTIF, MINIFS, MAXIFS)
 *    - Operate on arrays of values
 *    - Example: SUM({multiSelectInput})
 *    - Example: SUMIF({price} > 50, {quantity})
 *
 * Note: MIN and MAX can function as both row operators (min/max of two values)
 * and column operators (min/max of an array).
 *
 * All arithmetic operations use safe BigNumber-based functions to prevent
 * floating point precision errors (e.g., 0.1 + 0.2 = 0.3, not 0.30000000000000004).
 *
 * @param formula - The parsed formula structure to evaluate
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns The numeric result of the formula evaluation
 * @throws error if the formula uses table-specific features
 */
export function resolveFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): FormulaResultValue {
  // Validate that this formula is valid for FormV2 context
  validateFormV2Context(formula, userEnteredValues)

  // Evaluate the formula based on its operator
  return resolveFormulaByOperator(formula, userEnteredValues)
}

/**
 * Validates that a formula only uses features available in FormV2 context.
 *
 * FormV2 formulas can use:
 * - Row operators: arithmetic, rounding
 * - Column operators: aggregations (SUM, COUNT, COUNT_DISTINCT, AVERAGE, MIN, MAX)
 * - Column operators with filters: conditional aggregations (SUMIF, COUNTIF, AVERAGEIF, MINIFS,
 *   MAXIFS)
 * - Form input references
 * - Nested formulas
 * - Conditional logic (if-then-else)
 *
 * @param formula - The formula to validate
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @throws Error if the formula uses unsupported features
 */
function validateFormV2Context(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): void {
  // Aggregations over several source columns pair their operands by source row, which a form's
  // parallel inputs cannot guarantee. Checked before the arity guard below so the error names the
  // real reason rather than the operand count.
  if (formula.operator === FormulaOperator.SUMPRODUCT) {
    throw new Error(`${formula.operator} is only available in table formulas`)
  }

  // Rounding operators (CEIL, FLOOR) operate on a single value; ROUND alone takes an optional
  // second value, the decimal digit count
  if (isRoundingFormulaOperator(formula.operator)) {
    const maxValues = formula.operator === FormulaOperator.ROUND ? 2 : 1
    if (formula.formulaValues.length < 1 || formula.formulaValues.length > maxValues) {
      const requirement = maxValues === 1 ? 'exactly 1 value' : '1 or 2 values'
      throw new Error(
        `Rounding operator ${formula.operator} requires ${requirement}, ` +
          `got ${formula.formulaValues.length}`,
      )
    }
  }

  // Column operators can work on a single array value
  // Note: MIN/MAX/AVERAGE can also work on multiple individual values when used as row operators
  if (isColumnFormulaOperator(formula.operator) && !isRowFormulaOperator(formula.operator)) {
    if (formula.formulaValues.length !== 1) {
      throw new Error(
        `Column operator ${formula.operator} requires exactly 1 array value, ` +
          `got ${formula.formulaValues.length}`,
      )
    }
  }

  // Recursively validate nested formulas and conditional formulas
  formula.formulaValues.forEach((formulaValue) => {
    // Validate nested formulas (e.g., formulas within formulas)
    if (formulaValue.formula) {
      validateFormV2Context(formulaValue.formula, userEnteredValues)
    }

    // Validate formulas within conditional branches (if-then-else logic)
    if (formulaValue.conditions) {
      formulaValue.conditions.forEach((condition) => {
        validateFormV2Context(condition.formula, userEnteredValues)
      })
    }
  })
}

/**
 * Extracts all form input IDs referenced in a logic expression.
 * Used to determine which arrays need to be aligned when filtering.
 *
 * @param expression - The logic expression to extract input IDs from
 * @returns Set of form input IDs referenced in the expression
 */
function extractReferencedInputIds(expression: LogicExpressionInput): Set<string> {
  const inputIds = new Set<string>()

  // Extract from expression values
  expression.values.forEach((value) => {
    if (value.formInputId) {
      inputIds.add(value.formInputId)
    }
    if (value.columnId) {
      inputIds.add(value.columnId)
    }
    if (value.nestedExpression) {
      const nestedIds = extractReferencedInputIds(value.nestedExpression)
      nestedIds.forEach((id) => inputIds.add(id))
    }
    if (value.formula) {
      const formulaIds = extractReferencedInputIdsFromFormula(value.formula)
      formulaIds.forEach((id) => inputIds.add(id))
    }
  })

  return inputIds
}

/**
 * Extracts all form input IDs referenced in a formula.
 *
 * @param formula - The formula to extract input IDs from
 * @returns Set of form input IDs referenced in the formula
 */
export function extractReferencedInputIdsFromFormula(formula: FormulaInput): Set<string> {
  const inputIds = new Set<string>()

  formula.formulaValues.forEach((value) => {
    if (value.formInputId) {
      inputIds.add(value.formInputId)
    }
    if (value.formula) {
      const nestedIds = extractReferencedInputIdsFromFormula(value.formula)
      nestedIds.forEach((id) => inputIds.add(id))
    }
    if (value.conditions) {
      value.conditions.forEach((condition) => {
        const conditionIds = extractReferencedInputIds(condition.condition)
        conditionIds.forEach((id) => inputIds.add(id))
        const formulaIds = extractReferencedInputIdsFromFormula(condition.formula)
        formulaIds.forEach((id) => inputIds.add(id))
      })
    }
  })

  if (formula.columnOperationFilter) {
    const filterIds = extractReferencedInputIds(formula.columnOperationFilter)
    filterIds.forEach((id) => inputIds.add(id))
  }

  return inputIds
}

/**
 * Filters an array by evaluating a condition for each element at the same index.
 * This implements the filtering logic for SUMIF, COUNTIF, MINIFS, MAXIFS.
 *
 * For each index i in the array to aggregate:
 * 1. Create a temporary context where each array-valued input is replaced with its value at index i
 * 2. Evaluate the condition with this context
 * 3. If the condition is true, include the element at index i in the result
 *
 * Example: SUMIF({price} > 50, {quantity})
 * - price = [60, 30, 75]
 * - quantity = [5, 10, 3]
 * - For i=0: price[0]=60 > 50 ✓ → include quantity[0]=5
 * - For i=1: price[1]=30 > 50 ✗ → exclude quantity[1]=10
 * - For i=2: price[2]=75 > 50 ✓ → include quantity[2]=3
 * - Result: [5, 3]
 *
 * @param arrayToAggregate - The array of values to aggregate (after filtering)
 * @param condition - The logic expression to evaluate for each element
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns Filtered array containing only elements where condition evaluated to true
 */
function filterArrayByCondition(
  arrayToAggregate: ActionDataArray,
  condition: LogicExpressionInput,
  userEnteredValues: UserEnteredLogicInputs,
  sourceFormInputId?: string,
): ActionDataArray {
  // Extract all form input IDs referenced in the condition
  const referencedInputIds = extractReferencedInputIds(condition)
  const sourceRows =
    sourceFormInputId && !userEnteredValues[sourceFormInputId]
      ? collectGroupedInputValues(sourceFormInputId, userEnteredValues)
      : []

  // Get arrays for all referenced inputs
  const inputArrays: Record<string, ActionDataArray> = {}
  for (const inputId of sourceRows.length > 0 ? [] : referencedInputIds) {
    const arrayValues = collectArrayValuesForInput(inputId, userEnteredValues)

    inputArrays[inputId] = arrayValues
  }

  // Filter array by evaluating condition for each index
  // Build array of non-null values that match the condition
  const filteredValues: Array<string | number | boolean> = []

  for (let i = 0; i < arrayToAggregate.length; i++) {
    // Skip null values - formulas don't operate on nulls
    const currentValue = arrayToAggregate[i]
    if (currentValue === null || currentValue === undefined) {
      continue
    }

    // Create temporary context with values at index i
    const tempContext: UserEnteredLogicInputs = { ...userEnteredValues }

    for (const inputId of referencedInputIds) {
      const sourceRow = sourceRows[i]
      if (sourceRow) {
        // Resolve by the source's group/row key: compacted arrays lose alignment across blanks.
        tempContext[inputId] = userEnteredValues[
          `${sourceRow.inputGroupId}.${sourceRow.index}.${inputId}`
        ] ??
          userEnteredValues[inputId] ?? {
            data: null,
            lookupCellIds: null,
            documentIds: null,
            userIds: null,
          }
        continue
      }
      const array = inputArrays[inputId]
      if (i < array.length && array[i] !== null && array[i] !== undefined) {
        const value = array[i]

        // Override with the value at this index (as a scalar, not array)
        // Create properly typed TableCellData based on value type
        if (typeof value === 'number') {
          tempContext[inputId] = {
            data: { type: DossDataFormat.NUMBER, data: value },
            lookupCellIds: null,
            documentIds: null,
            userIds: null,
          }
        } else if (typeof value === 'string') {
          tempContext[inputId] = {
            data: { type: DossDataFormat.STRING, data: value },
            lookupCellIds: null,
            documentIds: null,
            userIds: null,
          }
        } else if (typeof value === 'boolean') {
          tempContext[inputId] = {
            data: { type: DossDataFormat.BOOLEAN, data: value },
            lookupCellIds: null,
            documentIds: null,
            userIds: null,
          }
        }
      }
    }

    // Evaluate condition with temporary context
    try {
      const resolvedCondition = resolveLogicExpressionFromParsed(condition, tempContext)

      const conditionMet = evaluateLogicExpression(resolvedCondition)

      if (conditionMet) {
        filteredValues.push(currentValue)
      }
    } catch (error) {
      console.error(`Error evaluating filter condition at index ${i}:`, error)
      // Skip this element if condition evaluation fails
      continue
    }
  }

  // Convert to properly typed ActionDataArray
  return getHomogeneousValues(filteredValues)
}

/**
 * Parses an input group cache key into its components.
 * Input group keys follow the pattern: inputGroupId.index.formInputId
 * @returns Parsed components or null if not a valid input group cache key
 * @example parseInputGroupCacheKey("group1.0.input-sku") // { inputGroupId: "group1", index: 0, formInputId: "input-sku" }
 */
function parseInputGroupCacheKey(
  key: string,
): { inputGroupId: string; index: number; formInputId: string } | null {
  const parts = key.split('.')
  if (parts.length !== 3) {
    return null
  }
  const [inputGroupId, indexStr, formInputId] = parts
  const index = parseInt(indexStr, 10)
  if (isNaN(index)) {
    return null
  }
  return { inputGroupId, index, formInputId }
}

/**
 * Collects all values from userEnteredValues that end with the specified formInputId.
 * This handles input groups where keys are structured as: inputGroupId.index.formInputId
 *
 * @param formInputId - The form input ID to search for
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns Array of values sorted by index, or empty array if no matches found
 */
function collectArrayValuesForInput(
  formInputId: string,
  userEnteredValues: UserEnteredLogicInputs,
): ActionDataArray {
  // First check for direct match (non-grouped input)
  if (userEnteredValues[formInputId]) {
    const userInput = userEnteredValues[formInputId]
    if (userInput && userInput.data) {
      const primitiveValue = getTableCellPrimitiveValue(userInput.data)

      // If it's already an array, return it directly
      if (Array.isArray(primitiveValue)) {
        return primitiveValue
      }

      // If it's a single value, wrap it in an array using getHomogeneousValues for type safety
      if (primitiveValue !== null && primitiveValue !== undefined) {
        return getHomogeneousValues([primitiveValue])
      }
    }
    return []
  }

  return getHomogeneousValues(
    collectGroupedInputValues(formInputId, userEnteredValues).map((entry) => entry.value),
  )
}

/** Collect nonblank values while retaining their original repeated-input row identity. */
function collectGroupedInputValues(
  formInputId: string,
  userEnteredValues: UserEnteredLogicInputs,
): { inputGroupId: string; index: number; value: ActionData }[] {
  const matchingEntries: Array<{
    inputGroupId: string
    index: number
    value: ActionData
  }> = []

  for (const key of Object.keys(userEnteredValues)) {
    const parsed = parseInputGroupCacheKey(key)
    if (parsed && parsed.formInputId === formInputId) {
      const userInput = userEnteredValues[key]
      if (userInput && userInput.data) {
        const primitiveValue = getTableCellPrimitiveValue(userInput.data)
        if (primitiveValue !== null && primitiveValue !== undefined) {
          matchingEntries.push({
            inputGroupId: parsed.inputGroupId,
            index: parsed.index,
            value: primitiveValue,
          })
        }
      }
    }
  }

  return matchingEntries.sort((a, b) => a.index - b.index)
}

/**
 * Extracts an array value from a formula value.
 * This is used by column operators (SUM, COUNT, MIN, MAX) that operate on arrays.
 *
 * Handles multiple cases:
 * - Form input that contains an array (e.g., multi-select field)
 * - Form input with a single numeric value (wrapped in array)
 * - Input groups where multiple values exist with keys like inputGroupId.index.formInputId
 * - Literal array values
 * - Nested formulas (result wrapped in array)
 *
 * @param formulaValue - The formula value to extract an array from
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns Array of numeric values, or empty array if value is null/undefined
 */
function extractArrayValue(
  formulaValue: FormulaValueInput,
  userEnteredValues: UserEnteredLogicInputs,
): ActionDataArray {
  // Handle form input references (most common case for arrays)
  if (formulaValue.formInputId) {
    const arrayValues = collectArrayValuesForInput(formulaValue.formInputId, userEnteredValues)

    return arrayValues
  }

  // Handle literal array values (less common but possible)
  const literalValue = formulaValue.valueNumber ?? formulaValue.value
  if (literalValue !== null && literalValue !== undefined) {
    return Array.isArray(literalValue) ? literalValue : [literalValue as number]
  }

  // Handle nested formulas (they return a single value, wrap in array)
  if (formulaValue.formula) {
    const result = resolveFormula(formulaValue.formula, userEnteredValues)
    // Narrowed per-branch so each wraps into a homogeneous number[]/string[] (ActionDataArray).
    return typeof result === 'number' ? [result] : [result]
  }

  // Handle conditional formulas (if-then-else returns single value, wrap in array)
  if (formulaValue.conditions && formulaValue.conditions.length > 0) {
    const result = evaluateConditionalFormula(formulaValue.conditions, userEnteredValues)
    // Narrowed per-branch so each wraps into a homogeneous number[]/string[] (ActionDataArray).
    return typeof result === 'number' ? [result] : [result]
  }

  return []
}

/**
 * Evaluates a single formula value, which could be:
 * - A literal number (e.g., 5)
 * - A form input reference (e.g., {inputId})
 * - A nested formula (e.g., {inputA} + {inputB})
 * - A conditional formula (if-then-else logic)
 *
 * @param formulaValue - The formula value to evaluate
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns The numeric result
 * @throws Error if the formula value is invalid or missing required data
 */
function resolveFormulaValue(
  formulaValue: FormulaValueInput,
  userEnteredValues: UserEnteredLogicInputs,
): FormulaResultValue {
  // Handle conditional formulas (if-then-else logic)
  // Example: IF({status} = "approved", {amount}, 0)
  if (formulaValue.conditions && formulaValue.conditions.length > 0) {
    return evaluateConditionalFormula(formulaValue.conditions, userEnteredValues)
  }

  // Handle nested formulas
  // Example: A formula value that is itself another formula like ({a} + {b})
  if (formulaValue.formula) {
    return resolveFormula(formulaValue.formula, userEnteredValues)
  }

  // Handle form input references
  // Example: {inputId} where inputId maps to a form input's value
  if (formulaValue.formInputId) {
    return resolveFormInputValue(formulaValue.formInputId, userEnteredValues)
  }

  // Handle TODAY/NOW
  // TODAY is a midnight-UTC DATE; NOW is a full DATE_TIME instant.
  // Checked before valueString since these nodes also mirror the token there.
  if (formulaValue.dateTimeToken === 'TODAY') {
    return dayJsToMidnightUTCDateTimeString(dayjs())
  }
  if (formulaValue.dateTimeToken === 'NOW') {
    return dayjs().toISOString()
  }

  // Handle string literal values
  // Example: The string "non-moo-meric"
  if (formulaValue.valueString !== null && formulaValue.valueString !== undefined) {
    return formulaValue.valueString
  }

  // Handle numeric literal values
  // Example: The number 5 or 3.14
  const literalValue = formulaValue.valueNumber ?? formulaValue.value
  if (literalValue !== null && literalValue !== undefined) {
    return literalValue
  }

  throw new Error('Formula value must have one of: value, formInputId, formula, or conditions')
}

/**
 * Evaluates conditional formulas by checking conditions in order and returning
 * the first matching result. This implements if-then-else logic.
 *
 * Example: IF({status} = "approved", {amount}, IF({status} = "pending", {amount} * 0.5, 0))
 *
 * @param conditions - Array of conditions to evaluate in order
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns The numeric result of the first matching condition's formula, or 0 if none match
 */
function evaluateConditionalFormula(
  conditions: FormulaConditionInput[],
  userEnteredValues: UserEnteredLogicInputs,
): FormulaResultValue {
  // Sort conditions by order to ensure proper evaluation sequence
  const sortedConditions = [...conditions].sort((a, b) => a.order - b.order)

  for (const condition of sortedConditions) {
    try {
      // Resolve and evaluate the condition logic expression
      const resolvedCondition = resolveLogicExpressionFromParsed(
        condition.condition,
        userEnteredValues,
      )

      const conditionMet = evaluateLogicExpression(resolvedCondition)

      // If this condition is true, return the result of its formula
      if (conditionMet) {
        return resolveFormula(condition.formula, userEnteredValues)
      }
    } catch (error) {
      console.error('Error evaluating formula condition:', error)
      // Continue to next condition if this one fails
      continue
    }
  }

  // If no conditions matched, return 0 as default
  return 0
}

/**
 * Resolves a form input reference to its value.
 * Also handles input groups where keys are structured as inputGroupId.index.formInputId.
 *
 * Conversion rules:
 * - number: returned as-is
 * - string: returned as-is (numeric coercion is left to the caller via toFormulaNumber)
 * - boolean: true = 1, false = 0
 * - array: sum of all numeric values
 * - null/undefined: 0
 * - grouped inputs: sum of all values across all indices
 *
 * @param formInputId - The ID of the form input to resolve
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns The value of the form input
 */
function resolveFormInputValue(
  formInputId: string,
  userEnteredValues: UserEnteredLogicInputs,
): FormulaResultValue {
  // Collect all values for this input (handles both direct and grouped inputs)
  const arrayValues = collectArrayValuesForInput(formInputId, userEnteredValues)

  if (arrayValues.length === 0) {
    return 0
  }

  // If there's only one value, convert it directly
  if (arrayValues.length === 1) {
    const value = arrayValues[0]

    // Number or string - return directly
    if (typeof value === 'number' || typeof value === 'string') {
      return value
    }

    // Boolean - convert to 1 or 0
    if (typeof value === 'boolean') {
      return value ? 1 : 0
    }

    // Null or undefined - treat as 0
    return 0
  }

  // Multiple values (grouped inputs) - sum all numeric values
  let sum = 0
  for (const value of arrayValues) {
    if (typeof value === 'number') {
      sum += value
    } else if (typeof value === 'string') {
      const parsed = parseFloat(value)
      if (!isNaN(parsed)) {
        sum += parsed
      }
    } else if (typeof value === 'boolean') {
      sum += value ? 1 : 0
    }
  }

  return sum
}

/**
 * Evaluates a SUM formula - sums all numbers in an array.
 * If a column operation filter is present (SUMIF), filters the array first.
 *
 * Example: SUM({multiSelectNumberField}) or SUM({arrayInput})
 * Example: SUMIF({price} > 50, {quantity})
 *
 * @param formula - The SUM formula to evaluate
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns The sum of all numeric values in the (optionally filtered) array
 * @throws Error if the formula doesn't have exactly 1 array value
 */
function evaluateSumFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): number {
  if (formula.formulaValues.length !== 1) {
    throw new Error(
      `SUM formula requires exactly 1 array value, got ${formula.formulaValues.length}`,
    )
  }

  let arrayValue = extractArrayValue(formula.formulaValues[0], userEnteredValues)

  // Apply column operation filter if present (SUMIF logic)
  if (formula.columnOperationFilter) {
    arrayValue = filterArrayByCondition(
      arrayValue,
      formula.columnOperationFilter,
      userEnteredValues,
    )
  }

  // Filter to only numeric values and sum them using safe addition
  const numbers = arrayValue.filter((val): val is number => typeof val === 'number')

  const result = safeSum(numbers)

  return result
}

/**
 * Evaluates a COUNT formula - counts the number of elements in an array.
 * If a column operation filter is present (COUNTIF), filters the array first.
 *
 * Example: COUNT({multiSelectField}) returns the number of selected options
 * Example: COUNTIF({status} = "Pending")
 *
 * @param formula - The COUNT formula to evaluate
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns The number of elements in the (optionally filtered) array
 * @throws Error if the formula doesn't have exactly 1 array value
 */
function evaluateCountFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): number {
  if (formula.formulaValues.length !== 1) {
    throw new Error(
      `COUNT formula requires exactly 1 array value, got ${formula.formulaValues.length}`,
    )
  }

  let arrayValue = extractArrayValue(formula.formulaValues[0], userEnteredValues)

  // Apply column operation filter if present (COUNTIF logic)
  if (formula.columnOperationFilter) {
    arrayValue = filterArrayByCondition(
      arrayValue,
      formula.columnOperationFilter,
      userEnteredValues,
    )
  }

  return arrayValue.length
}

/**
 * Evaluates a COUNT_DISTINCT formula - counts the distinct elements in an array.
 *
 * Example: COUNT_DISTINCT({multiSelectField}) returns the number of distinct selected options
 *
 * @param formula - The COUNT_DISTINCT formula to evaluate
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns The number of distinct elements in the array
 * @throws Error if the formula doesn't have exactly 1 array value
 */
function evaluateCountDistinctFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): number {
  if (formula.formulaValues.length !== 1) {
    throw new Error(
      `COUNT_DISTINCT formula requires exactly 1 array value, got ${formula.formulaValues.length}`,
    )
  }

  const arrayValue: (string | number | boolean)[] = [
    ...extractArrayValue(formula.formulaValues[0], userEnteredValues),
  ]

  // SQL's COUNT(DISTINCT …) ignores NULLs, so a blank entry is not a distinct value here either
  return new Set(arrayValue.filter((value) => value !== null && value !== undefined)).size
}

/**
 * Evaluates an AVERAGE formula — the mean of an array, or of two or more individual values.
 * If a column operation filter is present (AVERAGEIF), the array is filtered first.
 *
 * Two modes, matching MIN/MAX:
 * 1. Column mode: AVERAGE({arrayInput}) — the mean of the array's numeric values
 * 2. Row mode: AVERAGE({input1}, {input2}) — the mean of the resolved operands
 *
 * Column mode skips non-numeric entries entirely rather than counting them as 0, so blanks do not
 * drag the mean down. This matches the SQL aggregation in `@doss/common-z-tables`'s
 * formula-sql-generator, so table and form formulas stay in agreement.
 *
 * Example: AVERAGE({prices}), AVERAGEIF({isPremium} = true, {discount})
 *
 * @param formula - The AVERAGE formula to evaluate
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns The mean value. Column mode returns 0 for an empty array, matching the other
 *   numeric aggregations
 */
function evaluateAverageFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): number {
  // Column mode: single array argument
  if (formula.formulaValues.length === 1) {
    let arrayValue = extractArrayValue(formula.formulaValues[0], userEnteredValues)

    // Apply column operation filter if present (AVERAGEIF logic)
    if (formula.columnOperationFilter) {
      arrayValue = filterArrayByCondition(
        arrayValue,
        formula.columnOperationFilter,
        userEnteredValues,
        formula.formulaValues[0].formInputId ?? undefined,
      )
    }

    const numbers = arrayValue.filter((val): val is number => typeof val === 'number')

    if (numbers.length === 0) {
      return 0
    }

    return safeDivide(safeSum(numbers), numbers.length, 0)
  }

  // Row mode: two or more individual arguments (like AVERAGE(a, b, c))
  const sortedValues = _.orderBy(formula.formulaValues, (value) => value.order, 'asc')
  const numbers = sortedValues.map((value) =>
    toFormulaNumber(resolveFormulaValue(value, userEnteredValues)),
  )

  return safeDivide(safeSum(numbers), numbers.length, 0)
}

/** Whether MIN/MAX should pick the later ('max') or earlier ('min') operand. */
type MinMaxMode = 'max' | 'min'

/**
 * Picks the earliest ('min') or latest ('max') instant from a list of date/date-time ISO strings,
 * returning the selected string verbatim so its own DATE vs DATE_TIME format is preserved.
 */
function pickMinMaxDate(dates: string[], mode: MinMaxMode): string {
  return dates.reduce((best, current) => {
    const preferCurrent =
      mode === 'max'
        ? toFormulaDayjs(current).isAfter(toFormulaDayjs(best))
        : toFormulaDayjs(current).isBefore(toFormulaDayjs(best))
    return preferCurrent ? current : best
  })
}

/**
 * Evaluates a MAX/MIN formula — finds the min/max value across an array or between two values.
 * Handles both numeric and date/date-time operands: date operands (stored as ISO strings) are
 * compared as instants, and the selected value is returned verbatim so a DATE stays a DATE and a
 * DATE_TIME stays a DATE_TIME. If a column operation filter is present (MAXIFS/MINIFS), the array
 * is filtered first.
 *
 * Two modes:
 * 1. Column mode: MAX({arrayInput}) — finds the min/max value in the array
 * 2. Row mode: MAX({input1}, {input2}) — finds the min/max value between two operands
 *
 * Example: MAX({prices}), MAXIFS({discount}, {isPremium} = true, {quantity} >= 10)
 * Example: MIN(TODAY, {dueDate}) — the earlier of today and the entered due date
 *
 * @param formula - The MAX/MIN formula to evaluate
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @param mode - Whether to take the maximum or the minimum
 * @returns The min/max value: a number, or a date/date-time string for date operands. Numeric
 *   column mode returns 0 for an empty array (matching aggregation behavior)
 */
function evaluateMinMaxFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
  mode: MinMaxMode,
): FormulaResultValue {
  const pickNumber = mode === 'max' ? Math.max : Math.min

  // Column mode: single array argument
  if (formula.formulaValues.length === 1) {
    let arrayValue = extractArrayValue(formula.formulaValues[0], userEnteredValues)

    // Apply column operation filter if present (MAXIFS/MINIFS logic)
    if (formula.columnOperationFilter) {
      arrayValue = filterArrayByCondition(
        arrayValue,
        formula.columnOperationFilter,
        userEnteredValues,
      )
    }

    // Date arrays arrive as ISO strings; compare them as instants rather than coercing to numbers.
    const dates = arrayValue.filter((val): val is string => typeof val === 'string')
    if (dates.length > 0) {
      return pickMinMaxDate(dates, mode)
    }

    // Filter to only numeric values
    const numbers = arrayValue.filter((val): val is number => typeof val === 'number')

    if (numbers.length === 0) {
      return 0
    }

    return pickNumber(...numbers)
  }

  // Row mode: two individual arguments (like MAX(a, b))
  if (formula.formulaValues.length === 2) {
    const left = resolveFormulaValue(formula.formulaValues[0], userEnteredValues)
    const right = resolveFormulaValue(formula.formulaValues[1], userEnteredValues)

    // A string operand means both are dates (the type checker forbids mixing a date with a number);
    // compare them as instants and return the selected operand verbatim to keep its format.
    const dates = [left, right].filter((val): val is string => typeof val === 'string')
    if (dates.length > 0) {
      return pickMinMaxDate(dates, mode)
    }

    return pickNumber(toFormulaNumber(left), toFormulaNumber(right))
  }

  throw new Error(
    `${formula.operator} formula requires 1 array value or 2 individual values, got ${formula.formulaValues.length}`,
  )
}

/**
 * Evaluates a CONCAT formula joining two or more string operands, in `order`.
 *
 * Example: CONCAT({firstName}, {lastName}) or CONCAT({a}, {b}, {c})
 *
 * CONCAT is variadic, so this can accept 2+ operands.
 *
 * @param formula - The CONCAT formula to evaluate
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns All operands joined together
 * @throws Error if the formula has fewer than 2 values, or any operand resolves to a non-string
 */
function evaluateConcatFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): string {
  if (formula.formulaValues.length < 2) {
    throw new Error(
      `CONCAT formula requires at least 2 values, got ${formula.formulaValues.length}`,
    )
  }

  const sortedValues = _.orderBy(formula.formulaValues, (value) => value.order, 'asc')
  return sortedValues
    .map((value) => toFormulaString(resolveFormulaValue(value, userEnteredValues)))
    .join('')
}

/** Accepted formats for TO_DATE single argument. */
const TO_DATE_DEFAULT_FORMATS = [DATE_FILTER_STRING_FORMAT, 'YYYY/MM/DD', 'YYYY.MM.DD']

function parseFormulaDate(value: string, format?: string | string[]): dayjs.Dayjs {
  if (!format) {
    return dayjs.utc(value)
  }
  // dayjs.utc() takes one format at a time (unlike dayjs()), so try each and keep the first match.
  const candidates = _.castArray(format).map((entry) => dayjs.utc(value, entry, true))
  return candidates.find((candidate) => candidate.isValid()) ?? dayjs.utc(value, '', true)
}

function describeDateFormats(format: string | string[]): string {
  const formats = _.castArray(format)
  const quoted = formats.map((entry) => `"${entry}"`).join(', ')
  return formats.length === 1 ? ` using format ${quoted}` : ` using formats ${quoted}`
}

/**
 * Coerces a resolved formula value to the UTC dayjs instant it represents.
 */
function toFormulaDayjs(value: FormulaResultValue, format?: string | string[]): dayjs.Dayjs {
  const parsed = typeof value === 'string' ? parseFormulaDate(value, format) : undefined
  if (!parsed || !parsed.isValid()) {
    const usingFormat = format ? describeDateFormats(format) : ''
    throw new Error(`Cannot convert form input "${value}" to a date${usingFormat}`)
  }
  return parsed
}

/**
 * Evaluates YEAR/MONTH/DAY: extracts a UTC calendar component from a single date/date-time operand.
 */
function evaluateDatePartFormula(
  formula: FormulaInput,
  extract: (date: dayjs.Dayjs) => number,
  userEnteredValues: UserEnteredLogicInputs,
): number {
  if (formula.formulaValues.length !== 1) {
    throw new Error(
      `${formula.operator} formula requires exactly 1 value, got ${formula.formulaValues.length}`,
    )
  }
  return extract(toFormulaDayjs(resolveFormulaValue(formula.formulaValues[0], userEnteredValues)))
}

/** Evaluates DATEDIFF({dateA}, {dateB}, "unit") — the calendar difference in the given unit. */
function evaluateDatediffFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): number {
  const sortedValues = _.orderBy(formula.formulaValues, (value) => value.order, 'asc')
  if (sortedValues.length !== 3) {
    throw new Error(`DATEDIFF formula requires exactly 3 values, got ${sortedValues.length}`)
  }
  const unit = sortedValues[2].valueString
  if (!unit || !isValidDateUnit(unit)) {
    throw new Error(`DATEDIFF unit must be one of ${DATE_UNITS.join(', ')}`)
  }
  const from = toFormulaDayjs(resolveFormulaValue(sortedValues[0], userEnteredValues))
  const to = toFormulaDayjs(resolveFormulaValue(sortedValues[1], userEnteredValues))
  return from.diff(to, unit.toLowerCase() as dayjs.OpUnitType)
}

/**
 * Evaluates DATE(year, month, day), constructing a plain DATE from three number operands.
 * Out-of-range month/day roll over the way spreadsheet DATE() does (matching dayjs's own
 * normalization): month 13 → next January, day 0 → last day of the prior month, etc. Only the
 * year is range-checked, since dayjs emits a malformed string for a negative year rather than
 * rolling over.
 */
function evaluateDateFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): string {
  const sortedValues = _.orderBy(formula.formulaValues, (value) => value.order, 'asc')
  if (sortedValues.length !== 3) {
    throw new Error(`DATE formula requires exactly 3 values, got ${sortedValues.length}`)
  }
  const [year, month, day] = sortedValues.map((value) =>
    toFormulaNumber(resolveFormulaValue(value, userEnteredValues)),
  )
  if (year < 1 || year > 9999) {
    throw new Error(`DATE year must be between 1 and 9999, got ${year}`)
  }
  const date = dayjs
    .utc()
    .year(year)
    .month(month - 1)
    .date(day)
    .startOf('day')
  if (!date.isValid()) {
    throw new Error(`Cannot construct a date from year=${year}, month=${month}, day=${day}`)
  }
  return dayJsToMidnightUTCDateTimeString(date)
}

/**
 * Evaluates DATEADD({date}, {amount}, "unit") — shifts a date/date-time operand by `amount` of the
 * given calendar unit, preserving the operand's own type (a DATE input stays a DATE, a DATE_TIME
 * input stays a DATE_TIME).
 */
function evaluateDateaddFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): string {
  const sortedValues = _.orderBy(formula.formulaValues, (value) => value.order, 'asc')
  if (sortedValues.length !== 3) {
    throw new Error(`DATEADD formula requires exactly 3 values, got ${sortedValues.length}`)
  }
  const unit = sortedValues[2].valueString
  if (!unit || !isValidDateUnit(unit)) {
    throw new Error(`DATEADD unit must be one of ${DATE_UNITS.join(', ')}`)
  }
  const isDateTime =
    resolveAdditiveOperandType(sortedValues[0], userEnteredValues) === FormulaValueType.DATE_TIME
  const date = toFormulaDayjs(resolveFormulaValue(sortedValues[0], userEnteredValues))
  const amount = toFormulaNumber(resolveFormulaValue(sortedValues[1], userEnteredValues))
  const result = date.add(amount, unit.toLowerCase() as dayjs.ManipulateType)
  return isDateTime ? result.toISOString() : dayJsToMidnightUTCDateTimeString(result)
}

/**
 * Reads exactly `expectedCount` formula values in `order`, for the fixed-arity text operators
 * (TRIM, LEN, SUBSTITUTE, LEFT/RIGHT/MID) — each takes a specific number of positional
 * operands, unlike CONCAT's variadic 2+.
 * @throws Error if the formula doesn't have exactly `expectedCount` values
 */
function getFixedArityFormulaValues(
  formula: FormulaInput,
  expectedCount: number,
): FormulaValueInput[] {
  if (formula.formulaValues.length !== expectedCount) {
    throw new Error(
      `${formula.operator} formula requires exactly ${expectedCount} value(s), ` +
        `got ${formula.formulaValues.length}`,
    )
  }
  return _.orderBy(formula.formulaValues, (value) => value.order, 'asc')
}

/**
 * Reads formula values in `order` for operators with optional trailing arguments (TO_DATE).
 * @throws Error if the formula has fewer than `minCount` or more than `maxCount` values
 */
function getRangeArityFormulaValues(
  formula: FormulaInput,
  minCount: number,
  maxCount: number,
): FormulaValueInput[] {
  const count = formula.formulaValues.length
  if (count < minCount || count > maxCount) {
    throw new Error(
      `${formula.operator} formula requires between ${minCount} and ${maxCount} value(s), ` +
        `got ${count}`,
    )
  }
  return _.orderBy(formula.formulaValues, (value) => value.order, 'asc')
}

/**
 * Evaluates LEFT(text, count) — the first `count` characters of `text`. `count <= 0` returns an
 * empty string; a `count` beyond the string's length returns the whole string.
 */
function evaluateLeftFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): string {
  const [textValue, countValue] = getFixedArityFormulaValues(formula, 2)
  const text = toFormulaString(resolveFormulaValue(textValue, userEnteredValues))
  const count = toFormulaNumber(resolveFormulaValue(countValue, userEnteredValues))
  return count <= 0 ? '' : text.slice(0, count)
}

/**
 * Evaluates RIGHT(text, count) — the last `count` characters of `text`. `count <= 0` returns an
 * empty string; a `count` beyond the string's length returns the whole string.
 */
function evaluateRightFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): string {
  const [textValue, countValue] = getFixedArityFormulaValues(formula, 2)
  const text = toFormulaString(resolveFormulaValue(textValue, userEnteredValues))
  const count = toFormulaNumber(resolveFormulaValue(countValue, userEnteredValues))
  return count <= 0 ? '' : text.slice(Math.max(0, text.length - count))
}

/**
 * Evaluates MID(text, start, length) — `length` characters of `text` starting at the 1-indexed
 * `start` position (matching spreadsheet MID semantics). A `start` below 1 is clamped to 1; a
 * `length` <= 0 returns an empty string.
 */
function evaluateMidFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): string {
  const [textValue, startValue, lengthValue] = getFixedArityFormulaValues(formula, 3)
  const text = toFormulaString(resolveFormulaValue(textValue, userEnteredValues))
  const start = toFormulaNumber(resolveFormulaValue(startValue, userEnteredValues))
  const length = toFormulaNumber(resolveFormulaValue(lengthValue, userEnteredValues))
  if (length <= 0) {
    return ''
  }
  const startIndex = Math.max(0, start - 1)
  return text.slice(startIndex, startIndex + length)
}

/**
 * Evaluates TRIM(text) — removes leading and trailing whitespace.
 */
function evaluateTrimFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): string {
  const [textValue] = getFixedArityFormulaValues(formula, 1)
  return toFormulaString(resolveFormulaValue(textValue, userEnteredValues)).trim()
}

/**
 * Evaluates UPPER(text) — the string with every character upper-cased.
 */
function evaluateUpperFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): string {
  const [textValue] = getFixedArityFormulaValues(formula, 1)
  return toFormulaString(resolveFormulaValue(textValue, userEnteredValues)).toUpperCase()
}

/**
 * Evaluates LOWER(text) — the string with every character lower-cased.
 */
function evaluateLowerFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): string {
  const [textValue] = getFixedArityFormulaValues(formula, 1)
  return toFormulaString(resolveFormulaValue(textValue, userEnteredValues)).toLowerCase()
}

/**
 * Evaluates LEN(text) — the character length of the string.
 */
function evaluateLenFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): number {
  const [textValue] = getFixedArityFormulaValues(formula, 1)
  return toFormulaString(resolveFormulaValue(textValue, userEnteredValues)).length
}

/**
 * Evaluates SUBSTITUTE(text, search, replacement) — replaces every occurrence of `search` in
 * `text` with `replacement`. An empty `search` returns `text` unchanged (nothing to substitute).
 */
function evaluateSubstituteFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): string {
  const [textValue, searchValue, replacementValue] = getFixedArityFormulaValues(formula, 3)
  const text = toFormulaString(resolveFormulaValue(textValue, userEnteredValues))
  const search = toFormulaString(resolveFormulaValue(searchValue, userEnteredValues))
  const replacement = toFormulaString(resolveFormulaValue(replacementValue, userEnteredValues))
  return search === '' ? text : text.split(search).join(replacement)
}

/**
 * Whether a TO_TEXT operand is a plain DATE, which renders as YYYY-MM-DD rather than its stored
 * midnight-UTC timestamp.
 */
function toTextOperandIsPlainDate(
  operand: FormulaValueInput,
  userEnteredValues: UserEnteredLogicInputs,
): boolean {
  if (operand.formula) {
    return operand.formula.outputType === FormulaValueType.DATE
  }
  if (operand.conditions && operand.conditions.length > 0) {
    return operand.conditions.every(
      (condition) => condition.formula.outputType === FormulaValueType.DATE,
    )
  }
  if (operand.formInputId) {
    return userEnteredValues[operand.formInputId]?.data?.type === DossDataFormat.DATE
  }
  return false
}

/**
 * Evaluates TO_TEXT(value) — the text representation of a number, string, date, or date-time
 * operand.
 * DATE: YYYY-MM-DD)
 * DATE_TIME: full timestamp
 * NUMBER/STRING: String(value)
 */
function evaluateToTextFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): string {
  const [operand] = getFixedArityFormulaValues(formula, 1)
  const value = resolveFormulaValue(operand, userEnteredValues)
  if (typeof value === 'string' && toTextOperandIsPlainDate(operand, userEnteredValues)) {
    const date = dayjs.utc(value)
    if (date.isValid()) {
      return date.format(DATE_FILTER_STRING_FORMAT)
    }
  }
  return String(value)
}

/** Evaluates TO_NUMBER(value) — parses a string operand into a number (a number passes through). */
function evaluateToNumberFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): number {
  const [value] = getFixedArityFormulaValues(formula, 1)
  return toFormulaNumber(resolveFormulaValue(value, userEnteredValues))
}

/**
 * Evaluates TO_DATE(value[, format]) — parses a string operand into a plain DATE at UTC midnight.
 * `format` is a dayjs format string (e.g. 'DD/MM/YYYY'). Parsing is always strict, so anything
 * outside TO_DATE_DEFAULT_FORMATS — including date-times — must state its layout.
 */
function evaluateToDateFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): string {
  const [value, formatValue] = getRangeArityFormulaValues(formula, 1, 2)
  const format = formatValue
    ? toFormulaString(resolveFormulaValue(formatValue, userEnteredValues))
    : TO_DATE_DEFAULT_FORMATS
  const resolved = resolveFormulaValue(value, userEnteredValues)
  const date = toFormulaDayjs(resolved, format).startOf('day')
  return dayJsToMidnightUTCDateTimeString(date)
}

/**
 * Evaluates a numeric arithmetic formula with two or more operands.
 * Used for multiplication and division since these are never overloaded for dates.
 *
 * @param formula - The arithmetic formula to evaluate
 * @param operation - The operation to combine two operands, applied left-to-right across all values
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns The result of the arithmetic operation
 * @throws Error if the formula has fewer than 2 values
 */
function evaluateArithmeticFormula(
  formula: FormulaInput,
  operation: (a: number, b: number) => number,
  userEnteredValues: UserEnteredLogicInputs,
): number {
  if (formula.formulaValues.length < 2) {
    throw new Error(
      `Formula operator ${formula.operator} requires at least 2 values, got ${formula.formulaValues.length}`,
    )
  }

  // Sort formula values by order to ensure correct operand positions, then reduce left-to-right.
  // A chain like {a} - {b} - {c} parses as a single flat SUBTRACTION node (not nested pairs), so
  // this must fold all operands rather than assume exactly 2.
  const sortedValues = _.orderBy(formula.formulaValues, (value) => value.order, 'asc')

  const numbers = sortedValues.map((value) =>
    toFormulaNumber(resolveFormulaValue(value, userEnteredValues)),
  )

  return numbers.reduce((left, right) => operation(left, right))
}

/**
 * Resolves the type of a form input's entered value.
 * A DATE/DATE_TIME value is stored as a plain ISO string, so we need to read `DossDataFormat`
 * rather than the raw extracted primitive.
 */
function resolveFormInputArithmeticType(
  formInputId: string,
  userEnteredValues: UserEnteredLogicInputs,
): FormulaValueType {
  switch (userEnteredValues[formInputId]?.data?.type) {
    case DossDataFormat.DATE:
      return FormulaValueType.DATE
    case DossDataFormat.DATE_TIME:
      return FormulaValueType.DATE_TIME
    default:
      return FormulaValueType.NUMBER
  }
}

/**
 * Resolves the type of an ADDITION/SUBTRACTION operand (literal, form input reference, or nested formula).
 */
function resolveAdditiveOperandType(
  formulaValue: FormulaValueInput,
  userEnteredValues: UserEnteredLogicInputs,
): FormulaValueType | undefined {
  if (formulaValue.formInputId) {
    return resolveFormInputArithmeticType(formulaValue.formInputId, userEnteredValues)
  }
  if (formulaValue.formula) {
    return inferFormulaOutputType(
      formulaValue.formula,
      (id) => resolveFormInputArithmeticType(id, userEnteredValues),
      [],
    )
  }
  if (formulaValue.dateTimeToken === 'TODAY') {
    return FormulaValueType.DATE
  }
  if (formulaValue.dateTimeToken === 'NOW') {
    return FormulaValueType.DATE_TIME
  }
  if (typeof formulaValue.valueString === 'string') {
    return FormulaValueType.STRING
  }
  return FormulaValueType.NUMBER
}

/** An arithmetic operand paired with its resolved type, threaded through `evaluateAdditiveFormula`'s reduce. */
type AdditiveOperand = { value: FormulaResultValue; type: FormulaValueType | undefined }

/** Adds (or subtracts, for a negative count) whole days, in the same format DATE values are stored in. */
function addDaysToDate(dateString: string, days: number): string {
  return dayJsToMidnightUTCDateTimeString(dayjs.utc(dateString).add(days, 'day'))
}

/**
 * Combines one pair of ADDITION/SUBTRACTION operands.
 *
 * @throws Error whenever `resolveBinaryArithmetic` errors (DATE_TIME operand, two dates
 *   added, or a date subtracted from a number)
 */
function combineAdditiveOperands(
  operator: FormulaOperator,
  left: AdditiveOperand,
  right: AdditiveOperand,
): AdditiveOperand {
  const { type, error } = resolveBinaryArithmetic(operator, left.type, right.type)
  if (error) {
    throw new Error(error)
  }

  if (type === FormulaValueType.DATE) {
    if (left.type === FormulaValueType.DATE) {
      const days = toFormulaNumber(right.value)
      const signedDays = operator === FormulaOperator.SUBTRACTION ? -days : days
      return { value: addDaysToDate(left.value as string, signedDays), type: FormulaValueType.DATE }
    }
    const days = toFormulaNumber(left.value)
    return { value: addDaysToDate(right.value as string, days), type: FormulaValueType.DATE }
  }

  if (left.type === FormulaValueType.DATE && right.type === FormulaValueType.DATE) {
    const days = dayjs.utc(left.value as string).diff(dayjs.utc(right.value as string), 'day')
    return { value: days, type: FormulaValueType.NUMBER }
  }

  const leftNumber = toFormulaNumber(left.value)
  const rightNumber = toFormulaNumber(right.value)
  const value =
    operator === FormulaOperator.ADDITION
      ? safeSum([leftNumber, rightNumber])
      : safeSubtract(leftNumber, rightNumber)
  return { value, type: FormulaValueType.NUMBER }
}

/**
 * Evaluates ADDITION/SUBTRACTION.
 *
 * @param formula - The formula to evaluate
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns The result: a number for plain arithmetic or a date difference, a date string otherwise
 * @throws Error if the formula has fewer than 2 values, or an operand pairing invalid for date math
 */
function evaluateAdditiveFormula(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): FormulaResultValue {
  if (formula.formulaValues.length < 2) {
    throw new Error(
      `Formula operator ${formula.operator} requires at least 2 values, got ${formula.formulaValues.length}`,
    )
  }

  const sortedValues = _.orderBy(formula.formulaValues, (value) => value.order, 'asc')
  const operands: AdditiveOperand[] = sortedValues.map((formulaValue) => ({
    value: resolveFormulaValue(formulaValue, userEnteredValues),
    type: resolveAdditiveOperandType(formulaValue, userEnteredValues),
  }))

  return operands.reduce((left, right) => combineAdditiveOperands(formula.operator, left, right))
    .value
}

/**
 * Evaluates a formula based on its operator type.
 * Delegates to specific evaluation methods for each operator.
 *
 * @param formula - The formula to evaluate
 * @param userEnteredValues - Map of form input IDs to their entered values
 * @returns The numeric result of the formula evaluation
 */
function resolveFormulaByOperator(
  formula: FormulaInput,
  userEnteredValues: UserEnteredLogicInputs,
): FormulaResultValue {
  switch (formula.operator) {
    // Arithmetic operators (row-level)
    case FormulaOperator.ADDITION:
      // Single value: used when a formula-in-logic is one expression (e.g. IFS(...) > 9)
      if (formula.formulaValues.length === 1) {
        return resolveFormulaValue(formula.formulaValues[0], userEnteredValues)
      }
      return evaluateAdditiveFormula(formula, userEnteredValues)
    case FormulaOperator.SUBTRACTION:
      return evaluateAdditiveFormula(formula, userEnteredValues)
    case FormulaOperator.MULTIPLICATION:
      return evaluateArithmeticFormula(formula, (a, b) => safeMultiply(a, b), userEnteredValues)
    case FormulaOperator.DIVISION:
      return evaluateArithmeticFormula(formula, (a, b) => safeDivide(a, b, 0), userEnteredValues)

    // Rounding operators (row-level)
    case FormulaOperator.ROUND: {
      // ROUND(n) rounds to the nearest whole number; ROUND(n, x) to x decimal digits (x < 0
      // rounds left of the decimal point). The single-arg form keeps its longstanding
      // Math.round semantics (ties toward +∞, so round(-2.5) === -2); only the two-arg form
      // uses safeRound, which rounds ties away from zero like Excel and the table SQL paths.
      const sortedValues = _.orderBy(formula.formulaValues, (value) => value.order, 'asc')
      const value = toFormulaNumber(resolveFormulaValue(sortedValues[0], userEnteredValues))
      if (sortedValues.length < 2) {
        return Math.round(value)
      }
      const digits = toFormulaNumber(resolveFormulaValue(sortedValues[1], userEnteredValues))
      return safeRound(value, digits)
    }
    case FormulaOperator.FLOOR:
      return Math.floor(
        toFormulaNumber(resolveFormulaValue(formula.formulaValues[0], userEnteredValues)),
      )
    case FormulaOperator.CEIL:
      return Math.ceil(
        toFormulaNumber(resolveFormulaValue(formula.formulaValues[0], userEnteredValues)),
      )

    // Aggregation operators (column-level)
    case FormulaOperator.SUM:
      return evaluateSumFormula(formula, userEnteredValues)
    case FormulaOperator.COUNT:
      return evaluateCountFormula(formula, userEnteredValues)
    case FormulaOperator.COUNT_DISTINCT:
      return evaluateCountDistinctFormula(formula, userEnteredValues)
    case FormulaOperator.AVERAGE:
      return evaluateAverageFormula(formula, userEnteredValues)
    // Rejected up front by the parser and by validateColumnFormulaInputForForm; this is the
    // backstop for a formula that reached evaluation some other way
    case FormulaOperator.SUMPRODUCT:
      throw new Error(`${formula.operator} is only available in table formulas`)
    case FormulaOperator.MAX:
      return evaluateMinMaxFormula(formula, userEnteredValues, 'max')
    case FormulaOperator.MIN:
      return evaluateMinMaxFormula(formula, userEnteredValues, 'min')

    // String operators (row-level)
    case FormulaOperator.CONCAT:
      return evaluateConcatFormula(formula, userEnteredValues)
    case FormulaOperator.LEFT:
      return evaluateLeftFormula(formula, userEnteredValues)
    case FormulaOperator.RIGHT:
      return evaluateRightFormula(formula, userEnteredValues)
    case FormulaOperator.MID:
      return evaluateMidFormula(formula, userEnteredValues)
    case FormulaOperator.TRIM:
      return evaluateTrimFormula(formula, userEnteredValues)
    case FormulaOperator.LEN:
      return evaluateLenFormula(formula, userEnteredValues)
    case FormulaOperator.SUBSTITUTE:
      return evaluateSubstituteFormula(formula, userEnteredValues)

    // DATE/DATE_TIME operators (row-level)
    case FormulaOperator.YEAR:
      return evaluateDatePartFormula(formula, (date) => date.year(), userEnteredValues)
    case FormulaOperator.MONTH:
      // dayjs's month() is 0-indexed; formula MONTH() is 1-indexed (January = 1).
      return evaluateDatePartFormula(formula, (date) => date.month() + 1, userEnteredValues)
    case FormulaOperator.DAY:
      return evaluateDatePartFormula(formula, (date) => date.date(), userEnteredValues)
    case FormulaOperator.DATEDIFF:
      return evaluateDatediffFormula(formula, userEnteredValues)
    case FormulaOperator.DATE:
      return evaluateDateFormula(formula, userEnteredValues)
    case FormulaOperator.DATEADD:
      return evaluateDateaddFormula(formula, userEnteredValues)

    // Cross-type conversion operators (row-level)
    case FormulaOperator.TO_TEXT:
      return evaluateToTextFormula(formula, userEnteredValues)
    case FormulaOperator.TO_NUMBER:
      return evaluateToNumberFormula(formula, userEnteredValues)
    case FormulaOperator.TO_DATE:
      return evaluateToDateFormula(formula, userEnteredValues)

    case FormulaOperator.UPPER:
      return evaluateUpperFormula(formula, userEnteredValues)
    case FormulaOperator.LOWER:
      return evaluateLowerFormula(formula, userEnteredValues)
    case FormulaOperator.AND:
    case FormulaOperator.OR:
    case FormulaOperator.NOT:
    case FormulaOperator.EQUAL:
    case FormulaOperator.NOT_EQUAL:
    case FormulaOperator.GREATER_THAN:
    case FormulaOperator.GREATER_THAN_OR_EQUAL:
    case FormulaOperator.LESS_THAN:
    case FormulaOperator.LESS_THAN_OR_EQUAL:
    case FormulaOperator.IS_EMPTY:
    case FormulaOperator.IS_NOT_EMPTY:
    case FormulaOperator.CONTAINS:
    case FormulaOperator.DOES_NOT_CONTAIN:
    case FormulaOperator.CONTAINS_ANY:
    case FormulaOperator.CONTAINS_ALL:
    case FormulaOperator.DOES_NOT_CONTAIN_ANY:
    case FormulaOperator.IN:
    case FormulaOperator.NOT_IN:
      throw new Error(`Operator ${formula.operator} not supported`)
  }
}
